import React from "react";
import { toast } from "sonner";
import { Radio, UserMinus, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";

const DAYS_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

/** Painel Modo Plantão Diretor · Ao vivo (aba `live` do admin).
 * Props:
 * - live: payload de /admin/on-call/live
 * - onRefresh: recarrega live
 * - kickOnCall(user): tira do plantão
 * - bulkLive/setBulkLive: estado do convite em massa (para o WS animar linhas)
 */
export const LivePlantaoPanel = ({ live, onRefresh, kickOnCall, bulkLive, setBulkLive }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6" data-testid="admin-live-panel">
    <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <h2 className="font-display font-bold text-lg text-[#0B2B62]">Modo Plantão Diretor · Ao vivo</h2>
        <span className="text-xs text-slate-500 hidden sm:inline">atualiza a cada 30s</span>
      </div>
      <button onClick={onRefresh} data-testid="admin-live-refresh" className="text-xs px-2.5 py-1 border border-slate-200 rounded-md hover:bg-slate-50">Atualizar agora</button>
    </div>
    {!live ? (
      <div className="p-10 text-center text-slate-500 text-sm">Carregando painel ao vivo…</div>
    ) : (
      <>
        <div className="p-4 border-b border-slate-100 bg-slate-50 text-xs text-slate-600 flex flex-wrap items-center gap-x-6 gap-y-1">
          <div><b className="text-slate-900">{live.total}</b> consultor(es) em plantão agora</div>
          <div>Horário: <b className="text-slate-900">{String(live.current_hour).padStart(2,"0")}:00</b></div>
          <div>{DAYS_SHORT[live.current_weekday]}</div>
          {live.coverage_candidates?.length > 0 && (
            <div>Reserva agendada agora: <b className="text-slate-900">{live.coverage_candidates.length}</b></div>
          )}
        </div>
        {live.live.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="admin-live-empty">Nenhum consultor está em plantão neste momento.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {live.live.map((u) => (
              <div key={u.id} data-testid={`live-user-${u.id}`} className="p-4 flex items-center gap-4 hover:bg-slate-50">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-11 h-11 rounded-full object-cover border border-slate-200" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-semibold">{(u.name || "?").slice(0,1)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold text-[#0B2B62]">{u.name}</div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${u.scheduled_now ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                      {u.scheduled_now ? "Escalado" : "Voluntário"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {u.email}
                    {u.current_slot && ` · slot ${String(u.current_slot.start_hour).padStart(2,"0")}:00–${String(u.current_slot.end_hour).padStart(2,"0")}:00`}
                    {u.on_call_since && ` · desde ${new Date(u.on_call_since).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}`}
                  </div>
                </div>
                <button data-testid={`live-kick-${u.id}`} onClick={() => kickOnCall(u)}
                  className="px-3 py-1.5 border border-red-500 text-red-600 rounded-md text-xs font-semibold hover:bg-red-50 inline-flex items-center gap-1.5">
                  <UserMinus className="w-3.5 h-3.5" /> Tirar do plantão
                </button>
              </div>
            ))}
          </div>
        )}
        {live.coverage_candidates?.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-blue-50/40" data-testid="live-invite-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[11px] font-bold uppercase tracking-widest text-blue-800">Convidar reserva agendada para cobrir agora</div>
              <button data-testid="live-invite-bulk"
                onClick={async () => {
                  if (!window.confirm(`Convidar TODOS os ${live.coverage_candidates.length} reservas agendados agora? Primeiro que aceitar leva.`)) return;
                  try {
                    const { data } = await api.post("/admin/on-call/invite-bulk", { reason: "cobertura urgente (convite em massa)" });
                    toast.success(`${data.invited} convite(s) enviado(s) — primeiro a aceitar leva`);
                    setBulkLive({ bulk_id: data.bulk_id, invites: data.invites || [], winner: null });
                    if (data.invites?.some((i) => i.whatsapp_link)) {
                      const first = data.invites.find((i) => i.whatsapp_link);
                      toast.info("Abrir WhatsApp do 1º da fila?", { duration: 20000,
                        action: { label: "Abrir WhatsApp", onClick: () => window.open(first.whatsapp_link, "_blank", "noopener") } });
                    }
                    onRefresh();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
                }}
                className="px-3 py-1.5 border-2 border-blue-600 bg-blue-600 text-white rounded-md text-xs font-bold uppercase tracking-widest hover:bg-blue-700 inline-flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> Convidar TODOS
              </button>
            </div>
            {bulkLive?.bulk_id && (
              <div className="mb-3 rounded-md border-2 border-blue-500 bg-white p-3" data-testid="bulk-live-panel">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-blue-800 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    Convite em massa ativo · {bulkLive.invites.length} convidados
                  </div>
                  <button onClick={() => setBulkLive(null)} className="text-xs text-slate-500 hover:text-slate-800">Fechar</button>
                </div>
                {bulkLive.winner ? (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md text-sm">
                    ✅ <b>{bulkLive.winner.name}</b> aceitou às {new Date(bulkLive.winner.at).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}
                  </div>
                ) : (
                  <div className="mt-2 space-y-1">
                    {bulkLive.invites.map((iv) => (
                      <div key={iv.invite_id} data-testid={`bulk-invite-${iv.invite_id}`} className="flex items-center justify-between text-xs py-1">
                        <span className="text-slate-700">{iv.target_name}</span>
                        <span className="text-slate-400">aguardando…</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {live.coverage_candidates.map((c) => (
                <button key={c.id} data-testid={`live-invite-${c.id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/admin/on-call/invite/${c.id}`, { reason: "vaga aguardando cobertura" });
                      if (data.whatsapp_link) {
                        toast.success(`Convite enviado a ${c.name}`, { duration: 15000,
                          action: { label: "Abrir WhatsApp", onClick: () => window.open(data.whatsapp_link, "_blank", "noopener") } });
                      } else {
                        toast.success(`Convite enviado a ${c.name} (sem WhatsApp cadastrado)`);
                      }
                      onRefresh();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
                  }}
                  className="flex items-center gap-3 p-3 bg-white rounded-md border border-blue-200 hover:bg-blue-50 text-left transition-colors">
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold">{(c.name || "?").slice(0,1)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#0B2B62] truncate">{c.name}</div>
                    <div className="text-[10px] text-slate-500">Convidar → WhatsApp</div>
                  </div>
                  <MessageCircle className="w-4 h-4 text-blue-600" />
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

export default LivePlantaoPanel;
